import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import {
    Card,
    Toolbar,
    Grid,
    Button,
    Typography,
    Dialog,
    DialogContent,
    DialogActions,
    MenuItem,
    LinearProgress,
    IconButton,
    Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DocType from '../../components/document_type';
import axios from 'axios';
import ExtendedField from '../../components/text_field';
import ExtendedSelect from '../../components/select_field';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import { sandboxCss } from '../../helpers';
import Auth from '../../auth';
import ConfirmDialog from '../../components/confirm_dialog';
import Papa from 'papaparse';
import FileSaverBase from 'filesaver.js-npm';
import cronstrue from 'cronstrue';
import { Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import { AddCircleOutline } from "@mui/icons-material";
import InfoIcon from '@mui/icons-material/Info';
import ScheduleIcon from '../../images/schedule.svg';
import createDecorator from 'final-form-calculate';
import Moment from 'react-moment';
import { loadProfiles } from '../files/export';
import Helpers from '../../helpers';
import { headerSize } from '../../theme';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (established pattern, see
// reports.tsx/scorecards.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;
// filesaver.js-npm ships no type declarations (established pattern, see
// reports.tsx/scorecards.tsx).
const FileSaver: any = FileSaverBase;

const StyledTable = styled(EnhancedTable)({
    "& .MuiAccordion-heading": {
        display: "none !important"
    }
});

const reportDescriptions: Record<string, string> = {
    ClientActivityReport: "Lists document activity over the past 8 days, including documents, orders, open orders, shipments, and invoices.",
    DetailedOpenOrderReport: "Lists open orders by partner categorized into: <7 days, 7-14, 14-30, 30-60, >60 days.",
    DocumentAgingReport: "Lists incomplete documents by partner categorized into: <7 days, 7-14, 14-30, 30-60, >60 days.",
    FuncAckReport: "Lists documents missing a Functional Acknowledgement (997) from a partner after 48 hours.",
    InventoryUnmatchedReport: "Lists unmatched inventory items for all partners. Used only for retailers.",
    InventoryUpdateReport: "Lists inventory details by partner, including total items, total updates, matched items, matched updates, unmatched items, in-stock percentage, and last update.",
    MonthlyDocumentReport: "Monthly summary of all documents processed (orders, acknowledgements, shipments, invoices).",
    MonthlyOrderReport: "Monthly summary of all orders processed.",
    NewDocumentActivityReport: "Monitors expected document volume over a 24-hour period. If volume falls below a threshold (e.g., <400 orders), an alert is triggered.",
    OpenDocumentReport: "Lists open documents not yet completed. Similar to the aging report, limited to a max of 50 documents.",
    ScoreCardReport: "Weekly summary of partner performance: overall score, ship time, ship %, cancel %. Used only for retailers.",
    ShippedOrderAgingReport: "Lists orders with shipments applied but not all items have been shipped.",
    SubmittedDocumentReport: "Lists documents stuck in \"Submitted\" status for over 1 hour, indicating possible connection issues.",
    UnshippedOrderReport: "Lists orders that have not shipped within 72 hours of the Requested Ship Date, limited to a max of 50 orders.",
    CustomExportReport: "Exports documents to a CSV file using a customizable export template and provides a link in the generated event."
};

const reportNameOverrides: Record<string, string> = {
    DetailedOpenOrderReport: "Open Order Details",
    DocumentAgingReport: "Document Aging Report",
    FuncAckReport: "Overdue FuncAcks",
    InventoryUnmatchedReport: "Unmatched Inventory Report",
    InventoryUpdateReport: "Inventory Update Report",
    MonthlyOrderReport: "Monthly Order Report",
    NewDocumentActivityReport: "New Document Activity",
    OpenDocumentReport: "Overdue Documents",
    ShippedOrderAgingReport: "Shipped Order Aging Report",
    CustomExportReport: "Custom Report"
}

const reportDocumentTypes: Record<string, string> = {
    DetailedOpenOrderReport: "Order",
    MonthlyOrderReport: "Order",
    ShippedOrderAgingReport: "Order",
    UnshippedOrderReport: "Order"
};

// Formerly instance fields on the class; these are constant lookup maps and
// never mutated, so they live at module scope.
const thresholds: Record<string, boolean> = {
    MonthlyDocumentReport: true,
    MonthlyOrderReport: true,
    NewDocumentActivityReport: true
};
const documentTypes: Record<string, boolean> = {
    DocumentAgingReport: true,
    FailedDocumentReport: true,
    FuncAckReport: true,
    NewDocumentActivityReport: true,
    OpenDocumentReport: true,
    SubmittedDocumentReport: true,
    CustomExportReport: true
};
const exportProfiles: Record<string, boolean> = {
    CustomExportReport: true
};

type ReportDefinition = {
    Id: number;
    Description?: string;
    ReportName: string;
    Schedule?: string;
    ReportAlias?: string;
};

type ScheduledReport = {
    ReportId?: number;
    ReportName?: string;
    ReportAlias?: string;
    DocumentType?: string;
    Threshold?: any;
    ExportProfile?: string;
    Schedule?: string;
    Description?: string;
};

type ReportResultTable = {
    name: string;
    rows: any[][];
};

type MonitoringProps = {
    setTitle?: (title: string) => void;
};

type MonitoringState = {
    showEdit: boolean;
    showConfirm: boolean;
    disableConfirm: boolean;
    disableEdit: boolean;
    showReport: boolean;
    tables: ReportResultTable[];
    showReportDetails: boolean;
    profiles: any[];
    showError: boolean;
    refresh?: boolean;
    report?: ScheduledReport | null;
    definitions?: ReportDefinition[];
    reportNames?: string[];
    result?: string | null;
    confirmMessage?: string;
    error?: string;
};

const Monitoring = (props: MonitoringProps) => {
    const [state, setStateRaw] = useState<MonitoringState>({showEdit: false, showConfirm: false, disableConfirm: false, disableEdit: false, showReport: false, tables: [], showReportDetails: false, profiles: [], showError: false});

    // Mirror this.setState's shallow-merge semantics; a functional updater is
    // used wherever the class read from this.state while merging.
    const setState = useCallback((partial: Partial<MonitoringState> | ((prev: MonitoringState) => Partial<MonitoringState>)) => {
        setStateRaw(prev => ({...prev, ...(typeof partial === 'function' ? partial(prev) : partial)}));
    }, []);

    // Refs mirror the latest committed state so the stable callbacks below (and
    // getData, which the table may retain and re-invoke) read live values the
    // way this.state did on the class.
    const definitionsRef = useRef(state.definitions);
    definitionsRef.current = state.definitions;
    const profilesRef = useRef(state.profiles);
    profilesRef.current = state.profiles;

    useEffect(() => {
        if (props.setTitle) {
            props.setTitle('Scheduled Reports');
        }
        (async () => {
            let profiles = (await loadProfiles()).filter((r: any) => !(!r.Id));
            setState({profiles});
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async (report: ScheduledReport) => {
        try{
            setState({disableEdit: true});
            if(report.ReportName){
                await axios.put(Config.api + "/odata/Company/ReportConfigurations", report);
            }else{
                await axios.post(Config.api + "/odata/Company/ReportConfigurations", report);
            }
        }catch (e: any){
            let error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            setState({showError: true, error: error, disableEdit: false});
            return;
        }
        setState(prev => ({refresh: !prev.refresh, disableEdit: false, showEdit: false, report: null}));
    }
    const deleteReport = async (report: ScheduledReport) => {
        setState({disableConfirm: true});
        let error = null;
        try {
            await axios.delete(Config.api + "/odata/Company/ReportConfigurations", { data: report });
        } catch (e: any){
            error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
        }
        setState(prev => ({refresh: !prev.refresh, disableConfirm: false, showConfirm: false, report: null, showError: !(!error), error}));
    }
    const runReport = async (report: ScheduledReport) => {
        setState({showReport: true, report: report, result: null});
        let request = {documentType: report.DocumentType, reportId: report.ReportId, threshold: report.Threshold, exportProfile: report.ExportProfile};
        let result: any = (await axios.post(Config.api + "/odata/Company/Functions.RunReport", request)).data;
        let tables: ReportResultTable[] = [];
        if(result.Error){
            result = result.Error;
        }
        else if(result.Events && result.Events.length > 0){
            let html = document.createElement("html");
            html.innerHTML = result.Events[0].Body;
            let htmlTables = html.querySelectorAll("table") || [];
            let i = 0;
            htmlTables.forEach(table => {
                let headers = [...(table.querySelectorAll("th") || [])].map(r => r.innerText);
                if(headers.length === 0){
                    return;
                }
                let rawRows = [...(table.querySelectorAll("tr") || [])];
                let rows = [headers].concat(rawRows.map(r => [...(r.querySelectorAll("td") || [])].map(x => x.innerText))).filter(r => r && r.length > 0);
                tables.push({name: "Report " + (i+1), rows});
                i++;
            });
            result = sandboxCss(result.Events[0].Body, "detailsModalBody");
        }else{
            result = "This report either did not generate a result or the results are meant to be sent to your trading partner.";
        }
        setState({result: result, tables});
    }
    const enableThreshold = useCallback((reportId?: number | null) => {
        if(reportId === undefined || reportId === null){
            return false;
        }
        const definitions = definitionsRef.current;
        if(definitions){
            var def = definitions.find(r => r.Id === reportId);
            if(def && thresholds[def.ReportName]){
                return true;
            }
        }
        return false;
    }, []);
    const enableDocType = useCallback((reportId?: number | null) => {
        if(reportId === undefined || reportId === null){
            return false;
        }
        const definitions = definitionsRef.current;
        if(definitions){
            var def = definitions.find(r => r.Id === reportId);
            if(def && documentTypes[def.ReportName]){
                return true;
            }
        }
        return false;
    }, []);
    const enableExportProfile = useCallback((reportId?: number | null) => {
        if(reportId === undefined || reportId === null){
            return false;
        }
        const definitions = definitionsRef.current;
        if(definitions){
            var def = definitions.find(r => r.Id === reportId);
            if(def && exportProfiles[def.ReportName]){
                return true;
            }
        }
        return false;
    }, []);
    const showDeleteConfirm = (row: ScheduledReport) => {
        let message = `Are you sure you want to delete this ${row.ReportAlias}`;
        if(row.ReportAlias && row.ReportAlias.toLowerCase().endsWith("report")){
            message += "?";
        } else {
            message += " report?";
        }
        setState({showConfirm: true, disableConfirm: false, report: row, confirmMessage: message});
    }
    const print = () => {
        let report = state.report as ScheduledReport;
        let printWindow = window.open('', report.ReportAlias, 'height=400,width=600');
        printWindow.document.body.innerHTML = '<h1>' + report.ReportAlias  + '</h1>'
        + document.getElementById("reportContent").innerHTML;
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    }
    const exportTable = (name: string, rows: any[][]) => {
        let output = Papa.unparse(rows);
        let blob = new Blob([output], { type: "text/csv" });
        FileSaver.saveAs(blob, name + ".csv");
    }
    const parseSchedule = (schedule: string) => {
        try{
            let parsed = cronstrue.toString(schedule);
            if(/^At \d\d:\d\d (AM|PM)$/.test(parsed)){
                parsed = parsed.replace('At', 'Daily at');
            }
            return parsed;
        }catch{}
    }
    const onReportClick = async (report: ScheduledReport) => {
        setState({showReportDetails: true, report: {...report, Description: reportDescriptions[report.ReportName as string]}});
    }
    const calculator = useMemo(() => createDecorator(
        {
            field: /ReportAlias/,
            updates: (value: any, name: any, allValues: any) => {
                if(!value){
                    return;
                }
                let reports = (definitionsRef.current || []).filter(r => r.ReportAlias === value);
                let first: any = reports.filter(r => r.Id === allValues.ReportId || r.Schedule === allValues.Schedule)[0] || reports[0];
                return {
                    ReportId: first.Id,
                    DocumentType: allValues.DocumentType || 'Order',
                    ExportProfile: null,
                    Threshold: enableThreshold(first.Id) ? allValues.Threshold : null
                };
            }
        },
        {
            field: /DocumentType/,
            updates: (value: any, name: any, allValues: any) => {
                if(!value){
                    return;
                }
                let profile = ((profilesRef.current.filter((r: any) => r.DocumentType === value && r.Name === allValues.ExportProfile)[0] || {}) as any).Name || null;
                return {
                    ExportProfile: profile
                };
            }
        }
    ), [enableThreshold]);
    const newReport = () => {
        let firstReport = (state.definitions || [])[0];
        setState({showEdit: true, disableEdit: false, report: {ReportId: firstReport.Id, ReportAlias: firstReport.ReportAlias}});
    }
    const getApiType = (docType?: string) => {
        if(docType === 'AdvanceShipmentNotice'){
            return 'Shipment';
        }
        if(docType === 'POAck'){
            return 'Acknowledgement';
        }
        return docType;
    }

    let canManageSettings = Auth.hasPermission('settings/manage');
    let cols: any[] = [
      { id: 'ReportAlias', sortable: true, label: 'Report', template: (value: any, row: ScheduledReport) => <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}><span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{value} {row.ExportProfile ? `(${row.ExportProfile})` : ''}</span><IconButton style={{flexShrink: 0}} color='primary' onClick={() => onReportClick(row)} size="small"><InfoIcon fontSize="inherit" /></IconButton></span> },
      { id: 'DocumentType', sortable: true, label: 'Document Type', width: '15em', template: (value: any, row: ScheduledReport) => enableDocType(row.ReportId) || reportDocumentTypes[row.ReportName as string] ? <DocType docType={value || reportDocumentTypes[row.ReportName as string]}/>: '' },
      { id: 'Threshold', sortable: true, label: 'Threshold', width: '12em' },
      { id: 'Schedule', label: 'Schedule (UTC)'},
      { id: 'LastRunTime', type:'date', sortable: true, label: 'Last Ran (Your Time)', template: (value: any) => (value ? <Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment> : ''), width: '13em' }
    ];
    if(canManageSettings){
        cols.push({ command: 'commands', stopPropagation: 'true', width: '19em', template: (value: any, row: ScheduledReport) => (
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingRight: '24px'}}>
                <Button size='small' variant='outlined' onClick={() => runReport(row)}>Preview</Button>
                <Button size='small' variant='outlined' onClick={() => setState({showEdit: true, disableEdit: false, report: row})}>Edit</Button>
                <Button size='small' variant='outlined' onClick={() => showDeleteConfirm(row)}>Delete</Button>
            </div>
        )});
    }
    let config = {
        columns: cols,
      getData: async () => {
          let definitions = definitionsRef.current;
          if(!definitions){
              definitions = (await axios.get(Config.api + "/odata/Company/ReportDefinitions?$select=Id,Description,ReportName,Schedule&$orderby=Description")).data.value;
              definitions!.forEach(r => {
                r.ReportAlias = reportNameOverrides[r.ReportName] || r.Description;
                r.Schedule = parseSchedule(r.Schedule as string)
            });
              let reportNames = definitions!.map(r => r.ReportAlias as string)
              reportNames = reportNames.filter((r, i) => reportNames.indexOf(r) === i).sort((a, b) => a.localeCompare(b));
              setState({definitions, reportNames});
          }
          let reports: ScheduledReport[] = (await axios.get(Config.api + "/odata/Company/ReportConfigurations")).data.value;
          return reports.map(r => ({
              ...r,
              ReportName: definitions!.find(x => x.Id === r.ReportId)!.ReportName,
              ReportAlias: definitions!.find(x => x.Id === r.ReportId)!.ReportAlias,
              Schedule: definitions!.find(x => x.Id === r.ReportId)!.Schedule,
          }));
      },
      order: 'asc',
      orderBy: 'ReportAlias',
      keyField: (value: ScheduledReport) => value.ReportId + value.DocumentType,
      pageSize: 20,
      pageSizes: [10, 20, 50],
      refresh: state.refresh,
      toolbarClassName: '',
      hideToolbar: true
    }
    let creatingReport = !((state.report || {}) as ScheduledReport).ReportName;
    return (
        <Grid container spacing={2} style={{padding: "24px 14px 24px 14px"}}>
            <Grid size={12} style={{textAlign: 'left', display: 'flex', gap: '1em'}}>
                <Typography style={{fontSize: headerSize, fontWeight: '600'}}>Scheduled Reports</Typography>
                <div style={{flex: 1}}></div>
                {canManageSettings && <Button onClick={newReport} color='primary' variant='contained'><AddCircleOutline style={{marginRight: '10px'}}/>Add report</Button>}
                <Button component={Link} variant='outlined' to='/profile/notifications'>Notifications</Button>
                <Button component={Link} variant='outlined' to='/order-management/advanced-export'>Advanced Export</Button>
            </Grid>
            <Grid size={12} style={{display: 'flex'}}>
                    <Card style={{flex: 1}}>
                        <Alert severity="info">
                            <Typography style={{textAlign: 'left'}}><span style={{fontWeight: 'bold'}}>Permissions Needed:</span> Settings Management</Typography>
                            <Typography><span style={{fontWeight: 'bold'}}>Note:</span> These reports can be run across your organization but only users with the notification enabled will receive the report through email.</Typography>
                        </Alert>
                    </Card>
            </Grid>
            <Grid size={12}>
                <StyledTable config={config}/>
            </Grid>
            <ConfirmDialog open={state.showConfirm} onClose={() => setState({showConfirm: false, report: null})} message={state.confirmMessage} onConfirm={() => deleteReport(state.report as ScheduledReport)}/>
              <Dialog
                  maxWidth="sm"
                  fullWidth
                  open={state.showEdit}
                  onClose={(event: any, reason: string) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                      if (state.disableEdit) return;
                    }
                    setState({showEdit: false, report: null});
                  }}>
                  {state.report ?
                  <Form onSubmit={save}
                  decorators={[calculator]}
                  initialValues={state.report}
                  render={({ handleSubmit, pristine, invalid, values }: any) => (
                    <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                    <Toolbar className='lbtoolbar'>{creatingReport ? 'Add Report' : `Edit ${(state.report as ScheduledReport).ReportAlias}`}</Toolbar>
                  <DialogContent>
                  <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0', gap: '0.5em'}}>
                    {creatingReport && <Field style={{marginBottom: '0.5em'}} label='Report' component={ExtendedSelect} name='ReportAlias'>
                        {(state.reportNames || []).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </Field>}
                    {!(!values.ReportId) && <Typography style={{marginBottom: '0.5em'}}>
                        {reportDescriptions[(state.definitions || []).filter(r => r.Id === values.ReportId)[0].ReportName]}
                    </Typography>}
                    {enableDocType(values.ReportId) && <Field label='Document Type' component={ExtendedSelect} name='DocumentType'>
                        <MenuItem value='Order'>Order</MenuItem>
                        <MenuItem value='AdvanceShipmentNotice'>Shipment</MenuItem>
                        <MenuItem value='Invoice'>Invoice</MenuItem>
                        <MenuItem value='POAck'>Acknowledgement</MenuItem>
                        <MenuItem value='Return'>Return</MenuItem>
                    </Field>}
                    {enableExportProfile(values.ReportId) && <><Field label='Export Template' shrink={true} component={ExtendedSelect} name='ExportProfile'>
                        {state.profiles.filter((r: any) => r.DocumentType === getApiType(values.DocumentType)).map((r: any) => <MenuItem key={r.Name} value={r.Name}>{r.Name}</MenuItem>)}
                    </Field>
                    <Typography>You may select an export template that is shared with other users at your company. Export templates are configured on the <Link to='/order-management/advanced-export'>Advanced Export</Link> page.</Typography>
                    </>}
                    {!(!values.ReportId) && <Field disabled={!values.ReportAlias} label='Schedule (UTC)' component={ExtendedSelect} name='ReportId'>
                        {(state.definitions || []).filter(r => r.ReportAlias === values.ReportAlias).map(d => <MenuItem key={d.Id} value={d.Id}>{d.Schedule}</MenuItem>)}
                    </Field>}
                    {enableThreshold(values.ReportId) &&
                    <Field label='Threshold (# of documents to trigger report)' component={ExtendedField} type='number' name='Threshold'/>}
                    </div>
                  </DialogContent>
                  <DialogActions>
                    <Button variant='text' type='submit' disabled={pristine || invalid || state.disableEdit || (enableExportProfile(values.ReportId) && !values.ExportProfile)} color="primary">
                      Save
                    </Button>
                  </DialogActions>
                  </form>
              )}/> : ''}
                </Dialog>
                <Dialog
                    maxWidth="md"
                    fullWidth
                    open={state.showReport}
                    onClose={() => setState({showReport: false})}
                    >
                    <Toolbar className='lbtoolbar'>{state.report && state.report.ReportAlias}</Toolbar>
                    <DialogContent id="reportContent">
                    {state.result ?
                        <Typography className="detailsModalBody" dangerouslySetInnerHTML={{__html: state.result}}></Typography>
                     : <LinearProgress style={{height: '2em'}} variant='indeterminate'/>}
                 </DialogContent>
                 {!(!state.result) && <DialogActions>
                    {state.tables.map(r => <Button variant="text" key={r.name} onClick={() => exportTable(r.name, r.rows)}>Download {r.name}</Button>)}
                   <Button variant="text" color="primary" onClick={print}>Print</Button>
                 </DialogActions>}
                  </Dialog>
                  <Dialog
                    maxWidth="sm"
                    fullWidth
                    open={state.showReportDetails}
                    onClose={() => setState({showReportDetails: false})}
                    >
                    <Toolbar className='lbtoolbar'>{state.report && state.report.ReportAlias}</Toolbar>
                    <DialogContent id="reportContent">
                        <Typography>{state.report && state.report.Description}</Typography>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                            <Typography style={{color: '#9E9E9E', marginTop: '1em'}}><img style={{marginRight: '10px'}} src={ScheduleIcon} alt='Schedule'/>{state.report && state.report.Schedule} (UTC)</Typography>
                            {state.report && ((state.report.DocumentType && enableDocType(state.report.ReportId)) || reportDocumentTypes[state.report.ReportName as string]) && <Chip size='small' variant='outlined' label={<span style={{color: '#9E9E9E'}}><DocType docType={state.report.DocumentType || reportDocumentTypes[state.report.ReportName as string]}/>s</span>}/>}
                        </div>

                    </DialogContent>
                  </Dialog>
                  <Dialog open={state.showError} onClose={() => setState({showError: false})}>
                        <Toolbar className='lbtoolbar'>Error</Toolbar>
                        <DialogContent><Typography>{state.error}</Typography></DialogContent>
                  </Dialog>
        </Grid>
    );
}

export default Monitoring;
